import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Typography, CircularProgress, Tooltip } from "@material-ui/core";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import CloseIcon from "@material-ui/icons/Close";
import styled, { keyframes } from "styled-components";
import { fetchCrawlStatus, startCrawl } from "../../actions/crawls";
import { selectLastCrawlId } from "../../selectors/crawlIdSelector";

/* ── Polling hook ────────────────────────────────────────── */
function useCrawlPolling(lastCrawlId) {
    const dispatch = useDispatch();
    const crawlStatus = useSelector((s) => s.crawls.crawls[lastCrawlId]);
    const ref = useRef(null);

    useEffect(() => {
        if (!lastCrawlId) return;
        if (!crawlStatus) dispatch(fetchCrawlStatus(lastCrawlId));
        ref.current = setInterval(() => dispatch(fetchCrawlStatus(lastCrawlId)), 5000);
        return () => clearInterval(ref.current);
    }, [lastCrawlId]);

    useEffect(() => {
        if (crawlStatus?.status?.pendingPages === 0 && ref.current) {
            clearInterval(ref.current);
            ref.current = null;
        }
    }, [crawlStatus]);

    return crawlStatus;
}

/* ── Styles ──────────────────────────────────────────────── */
const pulse = keyframes`
    0%, 100% { opacity: 1; transform: scale(1); }
    50%       { opacity: .55; transform: scale(1.2); }
`;

const FloatRoot = styled.div`
    position: fixed;
    bottom: 24px;
    right: 24px;
    z-index: 1300;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 8px;
    pointer-events: none;
    & > * { pointer-events: auto; }
    @media (max-width: 480px) {
        bottom: 16px;
        right: 16px;
        left: 16px;
        align-items: stretch;
    }
`;

/* Detail card that slides in above the pill */
const DetailCard = styled.div`
    background: #fff;
    border-radius: 14px;
    border: 1px solid #e5e7eb;
    box-shadow: 0 8px 32px rgba(0,0,0,.14);
    width: 300px;
    overflow: hidden;
    transform-origin: bottom right;
    animation: fadeUp 150ms ease;
    @keyframes fadeUp {
        from { opacity: 0; transform: translateY(8px) scale(.97); }
        to   { opacity: 1; transform: translateY(0) scale(1); }
    }
    @media (max-width: 480px) {
        width: 100%;
    }
`;

const DetailHeader = styled.div`
    background: ${({ $crawling }) =>
        $crawling
            ? "linear-gradient(135deg, #78350f, #d97706)"
            : "linear-gradient(135deg, #1e3a8a, #6366f1)"};
    padding: 12px 14px;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const DetailTitle = styled(Typography)`
    color: #fff !important;
    font-weight: 700 !important;
    font-size: 0.85rem !important;
    flex: 1;
`;

const DetailBody = styled.div`
    padding: 12px 14px;
`;

const ProgressRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 5px;
`;

const ProgressTrack = styled.div`
    height: 6px;
    border-radius: 99px;
    background: #f0f0f0;
    overflow: hidden;
    margin-bottom: 12px;
`;

const ProgressFill = styled.div`
    height: 100%;
    border-radius: 99px;
    background: ${({ $crawling }) =>
        $crawling
            ? "linear-gradient(90deg, #f59e0b, #fbbf24)"
            : "linear-gradient(90deg, #6366f1, #8b5cf6)"};
    width: ${({ $pct }) => $pct}%;
    transition: width 800ms ease;
`;

const MetaGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
`;

const MetaCell = styled.div`
    background: #f9fafb;
    border-radius: 8px;
    padding: 8px 10px;
`;

const MetaLabel = styled(Typography)`
    font-size: 0.65rem !important;
    font-weight: 700 !important;
    color: #9ca3af !important;
    text-transform: uppercase;
    letter-spacing: 0.5px !important;
    margin-bottom: 2px !important;
`;

const MetaValue = styled(Typography)`
    font-size: 0.78rem !important;
    font-weight: 600 !important;
    color: #111827 !important;
    line-height: 1.3 !important;
`;

/* The compact floating pill */
const Pill = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    background: ${({ $crawling }) =>
        $crawling
            ? "linear-gradient(135deg, #78350f, #d97706)"
            : "linear-gradient(135deg, #1e3a8a, #6366f1)"};
    border-radius: 99px;
    padding: 10px 16px;
    box-shadow: 0 4px 20px rgba(0,0,0,.2);
    cursor: default;
    min-width: 0;
    @media (max-width: 480px) {
        border-radius: 14px;
        justify-content: space-between;
    }
`;

const StatusDot = styled.span`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ $crawling }) => ($crawling ? "#fbbf24" : "#34d399")};
    flex-shrink: 0;
    animation: ${({ $crawling }) => ($crawling ? pulse : "none")} 1.4s ease-in-out infinite;
`;

const PillLabel = styled(Typography)`
    color: rgba(255,255,255,0.85) !important;
    font-size: 0.78rem !important;
    font-weight: 600 !important;
    white-space: nowrap;
    flex: 1;
`;

const PillPct = styled(Typography)`
    color: #fff !important;
    font-size: 0.82rem !important;
    font-weight: 800 !important;
    white-space: nowrap;
    @media (max-width: 480px) { display: none; }
`;

const PillTrack = styled.div`
    width: 52px;
    height: 4px;
    border-radius: 99px;
    background: rgba(255,255,255,0.2);
    overflow: hidden;
    flex-shrink: 0;
    @media (max-width: 480px) { display: none; }
`;

const PillFill = styled.div`
    height: 100%;
    border-radius: 99px;
    background: rgba(255,255,255,0.85);
    width: ${({ $pct }) => $pct}%;
    transition: width 800ms ease;
`;

const PillBtn = styled.button`
    appearance: none;
    border: none;
    background: rgba(255,255,255,0.18);
    color: #fff;
    border-radius: 99px;
    padding: 4px 10px;
    font-size: 0.75rem;
    font-weight: 700;
    cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
    opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
    display: inline-flex;
    align-items: center;
    gap: 4px;
    transition: background 120ms;
    white-space: nowrap;
    flex-shrink: 0;
    &:hover:not(:disabled) { background: rgba(255,255,255,0.3); }
`;

const ExpandBtn = styled.button`
    appearance: none;
    border: none;
    background: rgba(255,255,255,0.15);
    color: #fff;
    border-radius: 50%;
    width: 26px;
    height: 26px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
    transition: background 120ms;
    padding: 0;
    &:hover { background: rgba(255,255,255,0.28); }
`;

/* Tiny dot shown when the bar is dismissed */
const MinDot = styled.button`
    appearance: none;
    border: none;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: ${({ $crawling }) =>
        $crawling
            ? "linear-gradient(135deg, #78350f, #d97706)"
            : "linear-gradient(135deg, #1e3a8a, #6366f1)"};
    box-shadow: 0 4px 16px rgba(0,0,0,.25);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;
    align-self: flex-end;
    transition: transform 120ms ease;
    &:hover { transform: scale(1.1); }
`;

/* ── Helpers ─────────────────────────────────────────────── */
function fmt(d) {
    if (!d) return "—";
    return new Date(d).toLocaleString(undefined, {
        day: "numeric", month: "short",
        hour: "2-digit", minute: "2-digit",
    });
}

function triggeredBy(trigger, owner) {
    return trigger?.type === "USER_INITIATED_WORKSPACE" ? (owner || "User") : "Scheduled";
}

/* ── Component ───────────────────────────────────────────── */
export default function CrawlerControlPanel() {
    const dispatch = useDispatch();
    const workspaces = useSelector((s) => s.workspaces);
    const lastCrawlId = useSelector(selectLastCrawlId);
    const crawlStatus = useCrawlPolling(lastCrawlId);
    const [expanded, setExpanded] = useState(false);
    const [dismissed, setDismissed] = useState(false);

    const onStart = () => dispatch(startCrawl(workspaces.current.seqNumber));
    const crawlStarting = workspaces.current?.crawlStarting;

    useEffect(() => {
        if (lastCrawlId) dispatch(fetchCrawlStatus(lastCrawlId));
    }, [lastCrawlId]);

    /* Derive display values */
    const status = crawlStatus?.status;
    const visitedPages = status?.visitedPages ?? 0;
    const pendingPages = status?.pendingPages ?? 0;
    const total = visitedPages + pendingPages;
    const isCrawling = Boolean(lastCrawlId) && (!status || pendingPages > 0);
    const pct = total > 0 ? Math.round((visitedPages / total) * 100) : (lastCrawlId && !isCrawling ? 100 : 0);
    const statusText = !lastCrawlId ? "No crawls yet" : isCrawling ? (visitedPages === 0 ? "Pending…" : `Crawling`) : "Done";
    const busy = isCrawling || crawlStarting;

    /* Re-show automatically if a new crawl starts while dismissed */
    useEffect(() => { if (isCrawling) setDismissed(false); }, [isCrawling]);

    if (dismissed) {
        return (
            <FloatRoot>
                <Tooltip title="Show crawler" placement="left">
                    <MinDot $crawling={isCrawling} onClick={() => setDismissed(false)}>
                        <StatusDot $crawling={isCrawling} />
                    </MinDot>
                </Tooltip>
            </FloatRoot>
        );
    }

    return (
        <FloatRoot>
            {/* ── Expanded detail card ── */}
            {expanded && status && (
                <DetailCard>
                    <DetailHeader $crawling={isCrawling}>
                        <StatusDot $crawling={isCrawling} />
                        <DetailTitle>Crawler details</DetailTitle>
                    </DetailHeader>
                    <DetailBody>
                        <ProgressRow>
                            <Typography variant="caption" style={{ color: "#6b7280" }}>
                                {visitedPages.toLocaleString()} / {total > 0 ? total.toLocaleString() : "—"} pages
                            </Typography>
                            <Typography variant="caption" style={{ fontWeight: 700, color: isCrawling ? "#d97706" : "#6366f1" }}>
                                {pct}%
                            </Typography>
                        </ProgressRow>
                        <ProgressTrack>
                            <ProgressFill $pct={pct} $crawling={isCrawling} />
                        </ProgressTrack>

                        {status.errorMessage && (
                            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
                                <ErrorOutlineIcon style={{ fontSize: 15, color: "#ef4444" }} />
                                <Typography variant="caption" style={{ color: "#ef4444" }}>{status.errorMessage}</Typography>
                            </div>
                        )}

                        <MetaGrid>
                            <MetaCell>
                                <MetaLabel>Visited</MetaLabel>
                                <MetaValue>{visitedPages.toLocaleString()}</MetaValue>
                            </MetaCell>
                            <MetaCell>
                                <MetaLabel>Pending</MetaLabel>
                                <MetaValue>{pendingPages.toLocaleString()}</MetaValue>
                            </MetaCell>
                            <MetaCell>
                                <MetaLabel>Started</MetaLabel>
                                <MetaValue>{fmt(crawlStatus.startedAt)}</MetaValue>
                            </MetaCell>
                            <MetaCell>
                                <MetaLabel>Completed</MetaLabel>
                                <MetaValue>{fmt(crawlStatus.completedAt)}</MetaValue>
                            </MetaCell>
                            <MetaCell>
                                <MetaLabel>Triggered by</MetaLabel>
                                <MetaValue>{triggeredBy(crawlStatus.trigger, crawlStatus.owner)}</MetaValue>
                            </MetaCell>
                            {!isCrawling && (
                                <MetaCell>
                                    <MetaLabel>Result</MetaLabel>
                                    <MetaValue style={{ display: "flex", alignItems: "center", gap: 3 }}>
                                        <CheckCircleOutlineIcon style={{ fontSize: 14, color: "#10b981" }} />
                                        Complete
                                    </MetaValue>
                                </MetaCell>
                            )}
                        </MetaGrid>
                    </DetailBody>
                </DetailCard>
            )}

            {/* ── Compact pill ── */}
            <Pill $crawling={isCrawling}>
                <StatusDot $crawling={isCrawling} />

                <PillLabel>{statusText}</PillLabel>

                {lastCrawlId && (
                    <>
                        <PillTrack>
                            <PillFill $pct={pct} />
                        </PillTrack>
                        <PillPct>{pct}%</PillPct>
                    </>
                )}

                <Tooltip title={busy ? "A crawl is already running" : lastCrawlId ? "Re-crawl" : "Start first crawl"}>
                    <PillBtn onClick={onStart} disabled={busy}>
                        {crawlStarting
                            ? <CircularProgress size={11} style={{ color: "#fff" }} />
                            : <PlayArrowIcon style={{ fontSize: 13 }} />}
                        {crawlStarting ? "Starting…" : "Crawl"}
                    </PillBtn>
                </Tooltip>

                {lastCrawlId && (
                    <Tooltip title={expanded ? "Hide details" : "Show details"}>
                        <ExpandBtn onClick={() => setExpanded((v) => !v)}>
                            {expanded
                                ? <ExpandMoreIcon style={{ fontSize: 16 }} />
                                : <ExpandLessIcon style={{ fontSize: 16 }} />}
                        </ExpandBtn>
                    </Tooltip>
                )}

                <Tooltip title="Dismiss">
                    <ExpandBtn onClick={() => { setDismissed(true); setExpanded(false); }}>
                        <CloseIcon style={{ fontSize: 14 }} />
                    </ExpandBtn>
                </Tooltip>
            </Pill>
        </FloatRoot>
    );
}
